import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, 
  CreditCard, 
  Zap, 
  TrendingUp, 
  Activity,
  DollarSign,
  Clock,
  FileText,
  BookOpen,
  RefreshCw,
  ChevronRight,
  AlertCircle,
  Database,
  Play,
  Globe,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingLangRequests, setPendingLangRequests] = useState(0);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchDashboard();
    // Fetch pending language request count
    axios.get(`${API_URL}/api/language-requests/admin?status=pending&limit=1`, { withCredentials: true })
      .then(res => setPendingLangRequests(res.data.pending_count || 0))
      .catch(() => {});
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API_URL}/api/admin/dashboard`, {
        withCredentials: true
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load admin dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-400">Failed to load dashboard data</p>
          <button 
            onClick={fetchDashboard}
            className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { user_stats, credit_stats, ai_usage_stats, daily_usage_today, content_stats, recent_users, recent_ai_usage } = data;

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "amber" }) => (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-400 text-sm">{title}</p>
          <p className={`text-2xl font-bold mt-1 text-${color}-400`}>{value}</p>
          {subtitle && <p className="text-slate-500 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-${color}-500/10`}>
          <Icon className={`w-5 h-5 text-${color}-500`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-slate-400 text-sm mt-1">Monitor user activity, credits, and AI usage</p>
          </div>
          <button
            onClick={fetchDashboard}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Language Requests red banner */}
        {pendingLangRequests > 0 && (
          <button
            data-testid="lang-requests-banner"
            onClick={() => navigate('/admin/language-requests')}
            className="w-full mb-6 flex items-center justify-between gap-3 px-5 py-4 bg-red-600/20 border border-red-500/50 rounded-xl text-left hover:bg-red-600/30 transition"
          >
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-300 font-semibold text-sm">
                  {pendingLangRequests} pending language request{pendingLangRequests !== 1 ? 's' : ''}
                </p>
                <p className="text-red-400/70 text-xs mt-0.5">
                  Users have submitted language requests that need review
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 text-red-400 text-sm font-medium flex-shrink-0">
              View Requests
              <ChevronRight className="w-4 h-4" />
            </div>
          </button>
        )}

        {/* User Stats Grid */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            User Statistics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard 
              icon={Users} 
              title="Total Users" 
              value={user_stats.total_users.toLocaleString()} 
              color="blue"
            />
            <StatCard 
              icon={TrendingUp} 
              title="New Today" 
              value={user_stats.new_users_today} 
              color="green"
            />
            <StatCard 
              icon={TrendingUp} 
              title="New (7d)" 
              value={user_stats.new_users_7d} 
              color="green"
            />
            <StatCard 
              icon={TrendingUp} 
              title="New (30d)" 
              value={user_stats.new_users_30d} 
              color="green"
            />
            <StatCard 
              icon={Activity} 
              title="Active Today" 
              value={user_stats.active_users_today} 
              color="amber"
            />
            <StatCard 
              icon={Activity} 
              title="Active (7d)" 
              value={user_stats.active_users_7d} 
              color="amber"
            />
          </div>
        </div>

        {/* Credit Stats */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-400" />
            Credit Statistics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              icon={CreditCard} 
              title="Total Credits in System" 
              value={credit_stats.total_credits_in_system.toLocaleString()} 
              color="purple"
            />
            <StatCard 
              icon={Users} 
              title="Avg Credits/User" 
              value={credit_stats.credits_per_user_avg} 
              color="purple"
            />
          </div>
        </div>

        {/* AI/Emergent Usage Stats */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Emergent AI Usage
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard 
              icon={Zap} 
              title="Total Requests" 
              value={ai_usage_stats.total_requests.toLocaleString()} 
              color="yellow"
            />
            <StatCard 
              icon={Clock} 
              title="Requests Today" 
              value={ai_usage_stats.requests_today} 
              color="yellow"
            />
            <StatCard 
              icon={TrendingUp} 
              title="Requests (7d)" 
              value={ai_usage_stats.requests_7d} 
              color="yellow"
            />
            <StatCard 
              icon={DollarSign} 
              title="Total Cost (USD)" 
              value={`$${ai_usage_stats.total_cost_usd.toFixed(2)}`}
              color="red"
            />
            <StatCard 
              icon={FileText} 
              title="Input Tokens" 
              value={(ai_usage_stats.total_input_tokens / 1000).toFixed(1) + 'K'} 
              color="slate"
            />
            <StatCard 
              icon={FileText} 
              title="Output Tokens" 
              value={(ai_usage_stats.total_output_tokens / 1000).toFixed(1) + 'K'} 
              color="slate"
            />
          </div>

          {/* AI Usage by Type */}
          {Object.keys(ai_usage_stats.by_type).length > 0 && (
            <div className="mt-4 bg-slate-900/50 border border-slate-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-slate-300 mb-3">Usage by Type</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(ai_usage_stats.by_type).map(([type, data]) => (
                  <div key={type} className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 capitalize">{type.replace(/_/g, ' ')}</p>
                    <p className="text-lg font-semibold text-white">{data.count}</p>
                    <p className="text-xs text-slate-500">${data.cost.toFixed(4)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Daily Usage Today */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-cyan-400" />
            Today's Feature Usage
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <StatCard 
              icon={Zap} 
              title="AI Questions" 
              value={daily_usage_today.ai_questions} 
              color="cyan"
            />
            <StatCard 
              icon={FileText} 
              title="Texts Generated" 
              value={daily_usage_today.texts_generated} 
              color="cyan"
            />
            <StatCard 
              icon={Activity} 
              title="TTS Requests" 
              value={daily_usage_today.tts_requests} 
              color="cyan"
            />
          </div>
        </div>

        {/* Content Stats */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-400" />
            Content Statistics
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <StatCard 
              icon={FileText} 
              title="Total Texts" 
              value={content_stats.total_texts.toLocaleString()} 
              color="emerald"
            />
            <StatCard 
              icon={BookOpen} 
              title="Words Tracked" 
              value={content_stats.total_words_tracked.toLocaleString()} 
              color="emerald"
            />
            <StatCard 
              icon={Activity} 
              title="Forge Progress Records" 
              value={content_stats.forge_progress_records.toLocaleString()} 
              color="emerald"
            />
          </div>
        </div>

        {/* Two Column Layout for Recent Data */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Users */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Recent Signups
              </h3>
              <button
                onClick={() => navigate('/admin/users')}
                className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1"
              >
                View All <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {recent_users.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="text-white text-sm font-medium">{u.name || 'No name'}</p>
                    <p className="text-slate-400 text-xs">{u.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-amber-400 text-sm font-medium">{u.credits} credits</p>
                    <p className="text-slate-500 text-xs">
                      {new Date(u.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent AI Usage */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-yellow-400" />
              Recent AI Usage
            </h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {recent_ai_usage.map((usage, idx) => (
                <div key={usage.usage_id || idx} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="text-white text-sm font-medium capitalize">{usage.type?.replace(/_/g, ' ') || 'Unknown'}</p>
                    <p className="text-slate-400 text-xs">{usage.user_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-yellow-400 text-sm font-medium">
                      {((usage.input_tokens || 0) + (usage.output_tokens || 0)).toLocaleString()} tokens
                    </p>
                    <p className="text-slate-500 text-xs">
                      ${(usage.estimated_cost_usd || 0).toFixed(4)}
                    </p>
                  </div>
                </div>
              ))}
              {recent_ai_usage.length === 0 && (
                <p className="text-slate-500 text-center py-4">No AI usage recorded yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── DO NOT EDIT WITHOUT TESTING — Lexicon Cache Preenrichment ───
   Wired to /api/admin/preenrich-top-words on backend. The panel polls
   /status every 3 seconds while a job is running so the admin can watch
   progress without refreshing the page. */
export function LexiconPreenrichPanel() {
  const [language, setLanguage] = useState('zh');
  const [top, setTop] = useState(3000);
  const [startFrom, setStartFrom] = useState(1);
  const [concurrency, setConcurrency] = useState(4);
  const [frequencyOnly, setFrequencyOnly] = useState(false);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [langs, setLangs] = useState([]);
  const [cacheStats, setCacheStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showLangStats, setShowLangStats] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [syncJob, setSyncJob] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [freqJob, setFreqJob] = useState(null);
  const [freqImporting, setFreqImporting] = useState(false);
  const [badEntries, setBadEntries] = useState(null); // list of bad cache entries

  // Pull the live language list from the DB so new languages added on the
  // backend automatically appear here without a code change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/languages/popular`, {
          withCredentials: true,
        });
        if (cancelled) return;
        const list = (res.data || [])
          .filter((l) => l && l.code)
          .map((l) => ({
            code: l.code,
            label: `${l.name || l.code.toUpperCase()} (${l.code})`,
          }));
        list.sort((a, b) => a.label.localeCompare(b.label));
        setLangs(list);
      } catch (err) {
        console.error('Failed to load language list for preenrichment:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const LANGS = langs;

  // Load cache stats
  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/lexicon-cache/stats`, {
        withCredentials: true,
      });
      setCacheStats(res.data);
      // Also load bad entries list when there are bad entries
      if ((res.data.no_definitions || 0) + (res.data.partial_timeout || 0) > 0) {
        const beRes = await axios.get(`${API_URL}/api/admin/lexicon-cache/bad-entries`, {
          withCredentials: true,
        });
        setBadEntries(beRes.data.entries || []);
      } else {
        setBadEntries([]);
      }
    } catch (err) {
      console.error('Failed to load cache stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  // Poll status while a job is running
  useEffect(() => {
    if (!job || (job.status !== 'running' && job.status !== 'cancelling')) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/preenrich-top-words/status?language=${job.language}`,
          { withCredentials: true }
        );
        setJob(res.data);
        // Refresh stats when job completes
        if (res.data.status === 'complete' || res.data.status === 'cancelled' || res.data.status === 'failed') {
          loadStats();
        }
      } catch (err) {
        console.error('preenrich status poll failed:', err);
      }
    }, 3000);
    return () => clearInterval(id);
  }, [job]);

  const start = async () => {
    setStarting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/preenrich-top-words?language=${language}&top=${top}&start_from=${startFrom}&concurrency=${concurrency}&frequency_only=${frequencyOnly}`,
        null,
        { withCredentials: true }
      );
      // Initial status
      const res = await axios.get(
        `${API_URL}/api/admin/preenrich-top-words/status?language=${language}`,
        { withCredentials: true }
      );
      setJob(res.data);
      toast.success(`Pre-enrichment started for ${language}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Failed to start: ${detail}`);
    } finally {
      setStarting(false);
    }
  };

  const cancelJob = async () => {
    if (!job) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/preenrich-top-words/cancel?language=${job.language}`,
        null,
        { withCredentials: true }
      );
      toast.success('Cancel requested');
    } catch (err) {
      toast.error('Failed to cancel');
    }
  };

  const cleanupCache = async () => {
    setCleaning(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lexicon-cache/cleanup?remove_partial=true&remove_empty=true`,
        null,
        { withCredentials: true }
      );
      toast.success(`Cleaned ${res.data.removed} low-quality entries`);
      loadStats();
    } catch (err) {
      toast.error('Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  const total = job?.total || 0;
  const done = (job?.enriched || 0) + (job?.skipped || 0) + (job?.refreshed || 0) + (job?.failed || 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  // Poll sync job while running
  useEffect(() => {
    if (!syncJob || (syncJob.status !== 'running')) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/sync-lexicon-cache/status`, { withCredentials: true });
        setSyncJob(res.data);
        if (res.data.status === 'complete' || res.data.status === 'failed') loadStats();
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(id);
  }, [syncJob]);

  const startSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API_URL}/api/admin/sync-lexicon-cache`, null, { withCredentials: true });
      const res = await axios.get(`${API_URL}/api/admin/sync-lexicon-cache/status`, { withCredentials: true });
      setSyncJob(res.data);
      toast.success('Cache sync started');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Sync failed: ${detail}`);
    } finally {
      setSyncing(false);
    }
  };

  // Poll freq import while running
  useEffect(() => {
    if (!freqJob || (freqJob.status !== 'running' && freqJob.status !== 'starting')) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/import-frequency-lists/status`, { withCredentials: true });
        setFreqJob(res.data);
        if (res.data.status === 'complete' || res.data.status === 'failed' || res.data.status === 'cancelled') {
          loadStats();
        }
      } catch { /* ignore */ }
    }, 2500);
    return () => clearInterval(id);
  }, [freqJob]);

  const startFreqImport = async () => {
    setFreqImporting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/import-frequency-lists`, null, { withCredentials: true });
      toast.success(res.data.message || 'Import started');
      const status = await axios.get(`${API_URL}/api/admin/import-frequency-lists/status`, { withCredentials: true });
      setFreqJob(status.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Import failed: ${detail}`);
    } finally {
      setFreqImporting(false);
    }
  };

  const cancelFreqImport = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/import-frequency-lists/cancel`, null, { withCredentials: true });
      toast.info('Cancel requested');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cancel failed');
    }
  };

  // Find current language stats from cacheStats
  const currentLangStats = cacheStats?.by_language?.find(l => l.language_code === language);

  return (
    <div className="mb-8 bg-slate-900/50 border border-slate-800 rounded-xl p-5">
      <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
        <Database className="w-5 h-5 text-cyan-400" />
        Lexicon Cache Pre-Enrichment
      </h2>
      <p className="text-slate-400 text-sm mb-4">
        Pre-fill <code className="px-1 rounded bg-slate-800">lexicon_cache</code> for the
        top-N most-frequent words of a language via Gemini. Job runs in the background.
      </p>

      {/* Cache Summary Bar */}
      {cacheStats && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-slate-800/60 rounded-lg p-3 text-center">
            <div className="text-cyan-400 text-xl font-bold">{cacheStats.total_cached.toLocaleString()}</div>
            <div className="text-slate-400 text-xs mt-0.5">Cached Words</div>
          </div>
          <div className="bg-slate-800/60 rounded-lg p-3 text-center">
            <div className="text-red-400 text-xl font-bold">{cacheStats.no_definitions}</div>
            <div className="text-slate-400 text-xs mt-0.5">No Definitions</div>
          </div>
          <div className="bg-slate-800/60 rounded-lg p-3 text-center">
            <div className="text-amber-400 text-xl font-bold">{cacheStats.partial_timeout}</div>
            <div className="text-slate-400 text-xs mt-0.5">Partial/Timeout</div>
          </div>
        </div>
      )}

      {/* Per-Language Coverage Toggle */}
      {cacheStats?.by_language?.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setShowLangStats(!showLangStats)}
            className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
          >
            <ChevronRight className={`w-3 h-3 transition-transform ${showLangStats ? 'rotate-90' : ''}`} />
            {showLangStats ? 'Hide' : 'Show'} per-language coverage ({cacheStats.by_language.length} languages)
          </button>
          {showLangStats && (
            <div className="mt-2 max-h-64 overflow-y-auto bg-slate-800/40 rounded-lg p-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-700">
                    <th className="text-left py-1">Language</th>
                    <th className="text-right py-1">Cached</th>
                    <th className="text-right py-1">AI</th>
                    <th className="text-right py-1">w/ Defs</th>
                    <th className="text-right py-1">Lexemes</th>
                    <th className="text-right py-1">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {cacheStats.by_language.map(l => (
                    <tr key={l.language_code} className="border-b border-slate-800/50 hover:bg-slate-700/20">
                      <td className="py-1 text-white">{l.language_name || l.language_code}</td>
                      <td className="py-1 text-right text-slate-300">{l.cached.toLocaleString()}</td>
                      <td className="py-1 text-right text-emerald-400">{(l.ai_enriched || 0).toLocaleString()}</td>
                      <td className="py-1 text-right text-cyan-400">{l.with_definitions.toLocaleString()}</td>
                      <td className="py-1 text-right text-slate-400">{l.total_lexemes.toLocaleString()}</td>
                      <td className="py-1 text-right">
                        <span className={`font-mono ${l.coverage_pct >= 5 ? 'text-emerald-400' : l.coverage_pct >= 1 ? 'text-amber-400' : 'text-red-400'}`}>
                          {l.coverage_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Cleanup Button + Bad Entries List */}
      {cacheStats && (cacheStats.no_definitions > 0 || cacheStats.partial_timeout > 0) && (
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={cleanupCache}
              disabled={cleaning}
              className="text-xs px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded-lg border border-red-600/30 transition disabled:opacity-50"
              data-testid="cleanup-cache-btn"
            >
              {cleaning ? 'Cleaning...' : `Clean ${cacheStats.no_definitions + cacheStats.partial_timeout} bad entries`}
            </button>
            <span className="text-slate-500 text-xs">Removes entries with no definitions or timeouts</span>
          </div>
          {badEntries && badEntries.length > 0 && (
            <div className="bg-slate-900/60 border border-red-900/30 rounded-lg p-3 max-h-48 overflow-y-auto">
              <div className="text-xs text-red-400 font-semibold mb-2">Bad entries ({badEntries.length})</div>
              <div className="grid grid-cols-1 gap-0.5">
                {badEntries.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-300 py-0.5 border-b border-slate-800/50 last:border-0">
                    <span className="font-mono text-amber-300 min-w-[80px]">{e.language}</span>
                    <span className="flex-1 truncate">{e.word}</span>
                    <span className="text-slate-500 text-[10px]">{e.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Controls Row */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Language</label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
            data-testid="preenrich-language-select"
          >
            {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Top N words</label>
          <input
            type="number"
            min={10}
            max={50000}
            value={top}
            onChange={(e) => setTop(parseInt(e.target.value) || 3000)}
            className="w-28 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
            data-testid="preenrich-top-input"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Start from rank</label>
          <input
            type="number"
            min={1}
            max={50000}
            value={startFrom}
            onChange={(e) => setStartFrom(parseInt(e.target.value) || 1)}
            className="w-28 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
            data-testid="preenrich-start-from-input"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Concurrency</label>
          <input
            type="number"
            min={1}
            max={8}
            value={concurrency}
            onChange={(e) => setConcurrency(parseInt(e.target.value) || 4)}
            className="w-20 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
            data-testid="preenrich-concurrency-input"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Only ranked</label>
          <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 cursor-pointer select-none" style={{ minHeight: 38 }}>
            <input
              type="checkbox"
              checked={frequencyOnly}
              onChange={e => setFrequencyOnly(e.target.checked)}
              data-testid="preenrich-frequency-only-checkbox"
              style={{ accentColor: '#06b6d4', width: 14, height: 14 }}
            />
            <span className="text-slate-300 text-xs">Frequency-ranked only</span>
          </label>
        </div>
        <button
          onClick={start}
          disabled={starting || (job && job.status === 'running')}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition disabled:opacity-50"
          data-testid="preenrich-start-btn"
        >
          <Play className="w-4 h-4" />
          {starting ? 'Starting...' : 'Start'}
        </button>
        {job && job.status === 'running' && (
          <button
            onClick={cancelJob}
            className="flex items-center gap-2 px-4 py-2 bg-red-600/80 hover:bg-red-600 text-white rounded-lg transition"
            data-testid="preenrich-cancel-btn"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Selected Language Info */}
      {currentLangStats && (
        <div className="mb-4 bg-slate-800/40 rounded-lg px-3 py-2 text-xs text-slate-300 flex items-center gap-4">
          <span>
            <span className="text-white font-medium">{currentLangStats.language_name || language}</span>:
          </span>
          <span>{currentLangStats.cached.toLocaleString()} cached</span>
          <span className="text-cyan-400">{currentLangStats.with_definitions.toLocaleString()} w/definitions</span>
          <span className="text-slate-400">{currentLangStats.total_lexemes.toLocaleString()} total lexemes</span>
          <span className={`font-mono font-bold ${currentLangStats.coverage_pct >= 5 ? 'text-emerald-400' : currentLangStats.coverage_pct >= 1 ? 'text-amber-400' : 'text-red-400'}`}>
            {currentLangStats.coverage_pct}% coverage
          </span>
        </div>
      )}

      {/* Job Progress */}
      {job && (
        <div className="bg-slate-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="text-slate-300">
              <span className="font-medium text-white">{job.language}</span> · ranks {job.start_from || 1}–{job.top} ·
              <span className={
                job.status === 'running' ? ' text-cyan-400 ml-2' :
                job.status === 'cancelling' ? ' text-amber-400 ml-2' :
                job.status === 'complete' ? ' text-green-400 ml-2' :
                job.status === 'cancelled' ? ' text-orange-400 ml-2' :
                ' text-red-400 ml-2'
              }>
                {job.status}
              </span>
            </span>
            <span className="text-slate-400">
              {job.elapsed_seconds}s elapsed
            </span>
          </div>
          {job.frequency_source && job.frequency_source !== 'unknown' && (
            <div className="text-xs text-slate-500 mb-2">Source: <span className="text-slate-300">{job.frequency_source}</span></div>
          )}
          <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden mb-3">
            <div
              className={`h-full transition-all ${job.status === 'cancelled' ? 'bg-orange-500' : job.status === 'failed' ? 'bg-red-500' : 'bg-cyan-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="grid grid-cols-5 gap-2 text-center text-xs">
            <div className="p-2 rounded bg-slate-900/60">
              <div className="text-slate-400">Total</div>
              <div className="text-white font-medium">{total}</div>
            </div>
            <div className="p-2 rounded bg-slate-900/60">
              <div className="text-slate-400">Enriched</div>
              <div className="text-cyan-400 font-medium">{job.enriched}</div>
            </div>
            <div className="p-2 rounded bg-slate-900/60">
              <div className="text-slate-400">Refreshed</div>
              <div className="text-amber-400 font-medium">{job.refreshed || 0}</div>
            </div>
            <div className="p-2 rounded bg-slate-900/60">
              <div className="text-slate-400">Skipped</div>
              <div className="text-slate-300 font-medium">{job.skipped}</div>
            </div>
            <div className="p-2 rounded bg-slate-900/60">
              <div className="text-slate-400">Failed</div>
              <div className="text-red-400 font-medium">{job.failed}</div>
            </div>
          </div>
          {job.error && (
            <p className="mt-3 text-red-400 text-xs">{job.error}</p>
          )}

          {/* Bad entries list */}
          {job.failed_entries?.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-red-400">
                  Bad entries ({job.failed_entries.length}{job.failed_entries.length >= 200 ? '+' : ''})
                </span>
              </div>
              <div className="max-h-40 overflow-y-auto rounded border border-red-900/40 bg-slate-900/60 divide-y divide-slate-800">
                {job.failed_entries.map((entry, i) => (
                  <div key={i} className="flex items-start gap-3 px-3 py-1.5 text-xs">
                    <span className="font-mono text-red-300 shrink-0">{entry.word}</span>
                    <span className="text-slate-500 truncate">{entry.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sync Cache — migrate word_lexicon → lexicon_cache */}
      <div className="mt-4 pt-4 border-t border-slate-800">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-white mb-0.5">Sync Cache from word_lexicon</p>
            <p className="text-xs text-slate-400">
              Copies all AI-enriched entries from <code className="bg-slate-800 px-1 rounded">word_lexicon</code> into{' '}
              <code className="bg-slate-800 px-1 rounded">lexicon_cache</code>. Safe: word_lexicon is never modified.
              Run this once to fix the current ai_enriched=0 mismatch.
            </p>
          </div>
          <button
            onClick={startSync}
            disabled={syncing || syncJob?.status === 'running'}
            className="shrink-0 text-sm px-3 py-1.5 bg-indigo-600/80 hover:bg-indigo-600 text-white rounded-lg border border-indigo-500/50 transition disabled:opacity-50"
            data-testid="sync-cache-btn"
          >
            {syncing ? 'Starting…' : syncJob?.status === 'running' ? 'Syncing…' : 'Sync Cache'}
          </button>
        </div>
        {syncJob && (
          <div className="mt-2 flex items-center gap-4 text-xs">
            <span className={syncJob.status === 'complete' ? 'text-green-400' : syncJob.status === 'failed' ? 'text-red-400' : 'text-cyan-400'}>
              {syncJob.status}
            </span>
            <span className="text-slate-400">{syncJob.synced || 0} / {syncJob.total || '?'} synced</span>
            <span className="text-slate-500">{syncJob.elapsed_seconds}s</span>
            {syncJob.error && <span className="text-red-400">{syncJob.error}</span>}
          </div>
        )}
      </div>

      {/* Import Frequency Lists */}
      <div className="mt-4 pt-4 border-t border-slate-800">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-white mb-0.5">Import Frequency Lists</p>
            <p className="text-xs text-slate-400">
              Downloads top-5050 word frequency lists for 26 languages from{' '}
              <a href="https://github.com/frekwencja/most-common-words-multilingual" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">wordfrequency.info</a>{' '}
              and upserts into <code className="bg-slate-800 px-1 rounded">word_master</code>.
              Safe and idempotent. Enables frequency-ordered preenrichment for all supported languages (2,000–5,050 words each).
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            {freqJob?.status === 'running' && (
              <button
                onClick={cancelFreqImport}
                className="text-sm px-3 py-1.5 bg-red-900/50 hover:bg-red-900 text-red-300 rounded-lg border border-red-700/50 transition"
                data-testid="freq-import-cancel-btn"
              >
                Cancel
              </button>
            )}
            <button
              onClick={startFreqImport}
              disabled={freqImporting || freqJob?.status === 'running' || freqJob?.status === 'starting'}
              className="text-sm px-3 py-1.5 bg-emerald-700/80 hover:bg-emerald-700 text-white rounded-lg border border-emerald-600/50 transition disabled:opacity-50"
              data-testid="freq-import-btn"
            >
              {freqImporting ? 'Starting…' : (freqJob?.status === 'running' || freqJob?.status === 'starting') ? 'Importing…' : 'Import Lists'}
            </button>
          </div>
        </div>
        {freqJob && (
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-4 text-xs">
              <span className={
                freqJob.status === 'complete' ? 'text-green-400' :
                freqJob.status === 'failed' ? 'text-red-400' :
                freqJob.status === 'cancelled' ? 'text-orange-400' : 'text-cyan-400'
              }>
                {freqJob.status}
              </span>
              <span className="text-slate-400">
                {freqJob.done_languages || 0} / {freqJob.total_languages || '?'} languages
              </span>
              <span className="text-slate-300">{(freqJob.total_words || 0).toLocaleString()} words imported</span>
              <span className="text-slate-500">{freqJob.elapsed_seconds}s</span>
            </div>
            {freqJob.current_language && freqJob.status === 'running' && (
              <div className="text-xs text-slate-500">
                Current: <span className="text-slate-300">{freqJob.current_language}</span>
              </div>
            )}
            {freqJob.failed_languages?.length > 0 && (
              <div className="text-xs text-amber-400">
                Partial failures: {freqJob.failed_languages.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Refresh stats */}
      <div className="mt-3 flex justify-end">
        <button
          onClick={loadStats}
          disabled={statsLoading}
          className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${statsLoading ? 'animate-spin' : ''}`} />
          Refresh stats
        </button>
      </div>
    </div>
  );
}
